import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Flame, User, Calendar, MapPin, AlertTriangle, QrCode, Lock } from "lucide-react";
import { freshnessColors, freshnessLabel, formatDate, isExpired, isAtRisk, boxLabel } from "../lib/format";
import { boxColorStyle } from "./BoxColorSwatch";

export const ContainerCard = ({ container }) => {
  const c = container;
  const colors = freshnessColors(c);
  const expired = isExpired(c);
  const atRisk = !expired && isAtRisk(c);
  // Box-color stripe — only render when the user actually picked a color
  // (anything other than "clear" / unset). Leaves untagged cards flat.
  const hasBoxColor = c.box_color && c.box_color !== "clear";
  const boxColor = hasBoxColor ? boxColorStyle(c.box_color).backgroundColor : null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, ease: "easeOut" }}
      whileHover={{ y: -4 }}
      data-testid={`container-card-${c.id}`}
      data-expired={expired ? "true" : "false"}
      className={`overflow-hidden rounded-2xl transition-shadow ${
        expired ? "tup-card-expired" : "tup-card"
      }`}
    >
      {hasBoxColor && (
        <div
          className="h-1.5 w-full"
          style={{ backgroundColor: boxColor }}
          data-testid={`box-color-bar-${c.id}`}
          aria-label={`Box colour: ${c.box_color}`}
        />
      )}
      <Link to={`/container/${c.id}`} className="block">
        <div className="p-6">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap mb-3">
                {boxLabel(c) && (
                  <span
                    className="inline-flex items-center px-2 py-1 rounded-md text-[11px] font-bold tracking-wide bg-[#DCEDF0] text-[#1F5566] border border-[#277A82]/30"
                    data-testid={`box-label-${c.id}`}
                  >
                    {boxLabel(c)}
                  </span>
                )}
                <div
                  className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold"
                  style={{ backgroundColor: colors.bg, color: colors.fg }}
                  data-testid={`freshness-badge-${c.id}`}
                >
                  {expired && (
                    <AlertTriangle
                      className="w-3.5 h-3.5 caution-anim"
                      strokeWidth={2.6}
                      data-testid={`expired-icon-${c.id}`}
                    />
                  )}
                  {atRisk && (
                    <AlertTriangle
                      className="w-3.5 h-3.5 text-[#D9883A]"
                      strokeWidth={2.4}
                      data-testid={`at-risk-icon-${c.id}`}
                    />
                  )}
                  {freshnessLabel(c)}
                </div>
              </div>
              <h3
                className={`font-heading text-xl font-semibold truncate ${
                  expired ? "text-[#8C2A0F]" : "text-[#2D3142]"
                }`}
              >
                {c.name}
              </h3>
              {c.notes ? (
                <p className="text-sm text-[#8A93A6] mt-1 line-clamp-1">{c.notes}</p>
              ) : null}
            </div>

            {c.logged_via_scan && (
              <QrCode
                className="shrink-0 w-9 h-9 text-[#4A8A78]"
                strokeWidth={2.2}
                data-testid={`scanned-badge-${c.id}`}
                aria-label="Logged via QR scan"
              />
            )}
          </div>

          <div className="mt-5 flex flex-wrap gap-2">
            {(c.dietary_tags || []).slice(0, 3).map((tag) => (
              <span
                key={tag}
                className="px-2.5 py-1 rounded-full text-[11px] font-semibold bg-[#DCEDF0] text-[#1F5566] border border-[#277A82]/20"
              >
                {tag}
              </span>
            ))}
          </div>

          <div className="mt-5 grid grid-cols-3 gap-3 text-xs">
            <div className="flex items-center gap-1.5 text-[#4F5D75]">
              <Calendar className="w-3.5 h-3.5" />
              <span>{formatDate(c.expiration_date)}</span>
            </div>
            <div className="flex items-center gap-1.5 text-[#4F5D75]">
              <Flame className="w-3.5 h-3.5" />
              <span>{c.calories_per_serving ? `${c.calories_per_serving} kcal` : "—"}</span>
            </div>
            <div className="flex items-center gap-1.5 text-[#4F5D75] justify-end">
              <User className="w-3.5 h-3.5" />
              <span className="truncate">{c.owner || "You"}</span>
              {c.is_personal && (
                <Lock
                  className="w-3 h-3 text-[#277A82] shrink-0"
                  data-testid={`personal-lock-${c.id}`}
                  aria-label="Personal storing"
                />
              )}
            </div>
          </div>

          <div
            className="mt-3 flex items-center gap-1.5 text-xs text-[#4F5D75]"
            data-testid={`location-${c.id}`}
          >
            <MapPin className="w-3.5 h-3.5 text-[#277A82]" />
            <span className="font-semibold text-[#1F5566]">Location:</span>
            <span className="truncate">{c.location_name || "Fridge"}</span>
          </div>

          <div
            className={`mt-4 pt-4 flex items-center justify-between border-t ${
              expired ? "border-[#D95D39]/30" : "border-[#E2E0D8]"
            }`}
          >
            <span className="font-mono text-[11px] text-[#8A93A6] tracking-wider">{c.qr_code}</span>
            <span className="text-xs font-semibold" style={{ color: colors.fg }}>
              View →
            </span>
          </div>
        </div>
      </Link>
    </motion.div>
  );
};
